import { CountrySelect, Input, Select } from '@components'
import { IBusiness, IInvoicingBankAccount } from '@query'
import { useBusinessInvoicingCountries, useInvoicingBankAccounts } from '@root/query/hooks'
import { compactIBAN, tryFormatIBAN } from '@root/utils/iban'
import React, { useEffect, useMemo, useState } from 'react'
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ConnectBankAlert } from './ConnectBankAlert'

export const getInvoicingBasicsRequiredDefaultValues = (data?: Partial<IBusiness>) => ({
  invoicing_email: data?.invoicing_email,
  invoicing_iban: data?.invoicing_iban,
  invoicing_bic: data?.invoicing_bic,
  invoicing_contact: data?.invoicing_contact,
  invoicing_street: data?.invoicing_street,
  invoicing_city: data?.invoicing_city,
  invoicing_postal_code: data?.invoicing_postal_code,
  invoicing_country: data?.invoicing_country
})

interface Props {
  register: UseFormRegister<Partial<IBusiness>>
  errors: FieldErrors<Partial<IBusiness>>
  setValue: UseFormSetValue<Partial<IBusiness>>
  watch: UseFormWatch<Partial<IBusiness>>
}

const displayName = (account: IInvoicingBankAccount): string => {
  if (account.name) {
    return `${account.bank_name} · ${account.name}`
  }
  return account.bank_name
}

const accountOptionLabel = (account: IInvoicingBankAccount): string => {
  const name = displayName(account)
  if (!account.iban) return name
  return `${name} · ${tryFormatIBAN(account.iban)}`
}

const findAccountByIban = (
  accounts: IInvoicingBankAccount[],
  iban: string | null | undefined
): IInvoicingBankAccount | undefined => {
  if (!iban) return undefined
  const compact = compactIBAN(iban)
  return accounts.find(account => account.iban && compactIBAN(account.iban) === compact)
}

export const InvoicingBasicsRequiredFields: React.FC<Props> = ({
  register,
  errors,
  setValue,
  watch
}) => {
  // React Hook Form mutates `formState.errors` in place. The compiler then keeps the previous inputs.
  'use no memo'
  const [t] = useTranslation()
  const { data: invoicingCountries } = useBusinessInvoicingCountries()
  const required = {
    required: { value: true as const, message: t('validation.required') }
  }

  const invoicingIban = watch('invoicing_iban')
  const invoicingBic = watch('invoicing_bic')
  const { data: accounts = [], isLoading: isBankAccountsLoading } = useInvoicingBankAccounts()

  const matchedAccount = useMemo(
    () => findAccountByIban(accounts, invoicingIban),
    [accounts, invoicingIban]
  )

  // undefined = user has not touched the selector; derive from saved IBAN.
  // null = user cleared the selector.
  const [selectedAccountId, setSelectedAccountId] = useState<string | null | undefined>(undefined)
  const effectiveSelectedId =
    selectedAccountId === undefined ? matchedAccount?.id ?? null : selectedAccountId

  const selectedAccount = accounts.find(account => account.id === effectiveSelectedId)

  const hasConnectedAccounts = accounts.length > 0
  const showMissingBicWarning = Boolean(selectedAccount) && !selectedAccount?.bic

  const applyAccount = (account: IInvoicingBankAccount | null) => {
    if (!account) {
      setSelectedAccountId(null)
      return
    }

    setValue('invoicing_bic', account.bic ?? '', { shouldDirty: true })
    setValue('invoicing_iban', account.iban ?? '', { shouldDirty: true, shouldValidate: true })
    setSelectedAccountId(account.id)
  }

  const copyMatchedBic = (account: IInvoicingBankAccount | undefined) => {
    if (!account?.bic) return
    setValue('invoicing_bic', account.bic, { shouldDirty: true, shouldValidate: true })
  }

  // Saved IBAN match: fill BIC once accounts load, only if the user has not cleared the selector.
  useEffect(() => {
    if (selectedAccountId !== undefined) return
    if (!matchedAccount?.bic || invoicingBic) return
    setValue('invoicing_bic', matchedAccount.bic, { shouldDirty: true, shouldValidate: true })
  }, [selectedAccountId, matchedAccount, invoicingBic, setValue])

  const { onChange: onIbanChange, ...ibanRegister } = register('invoicing_iban', required)

  return (
    <>
      <FormSectionHeading>{t('settings.invoicing.paymentTitle')}</FormSectionHeading>

      {!isBankAccountsLoading && !hasConnectedAccounts && <ConnectBankAlert />}

      {hasConnectedAccounts && (
        <Select
          label={t('settings.invoicing.bankAccount.label')}
          value={effectiveSelectedId ?? ''}
          disabled={isBankAccountsLoading}
          onChange={event => {
            const accountId = event.target.value
            if (!accountId) {
              applyAccount(null)
              return
            }
            const account = accounts.find(item => item.id === accountId)
            if (account) {
              applyAccount(account)
            }
          }}
        >
          <option value=""></option>
          {accounts.map(account => (
            <option key={account.id} value={account.id}>
              {accountOptionLabel(account)}
            </option>
          ))}
        </Select>
      )}

      <Input
        label={t('settings.invoicing.iban')}
        errors={errors.invoicing_iban}
        required
        {...ibanRegister}
        onChange={event => {
          void onIbanChange(event)
          copyMatchedBic(findAccountByIban(accounts, event.target.value))
        }}
        disabled={Boolean(selectedAccount?.iban)}
      />
      <Input
        label={t('settings.invoicing.bic')}
        errors={errors.invoicing_bic}
        required
        placeholder={
          showMissingBicWarning ? t('settings.invoicing.bankAccount.bicPlaceholder') : undefined
        }
        {...register('invoicing_bic', required)}
        disabled={Boolean(selectedAccount?.bic)}
      />
      {showMissingBicWarning && (
        <MissingBicHint>{t('settings.invoicing.bankAccount.missingBic')}</MissingBicHint>
      )}

      <FormSectionHeading>{t('settings.invoicing.invoicingAddressTitle')}</FormSectionHeading>
      <Input
        label={t('general.address.email')}
        required
        info={t('settings.invoicing.emailInfo')}
        errors={errors.invoicing_email}
        {...register('invoicing_email', required)}
      />
      <Input
        label={t('settings.invoicing.contactPerson')}
        errors={errors.invoicing_contact}
        {...register('invoicing_contact')}
      />
      <Input
        label={t('general.address.street')}
        required
        errors={errors.invoicing_street}
        {...register('invoicing_street', required)}
      />
      <InputRow>
        <Input
          label={t('general.address.postalCode')}
          required
          {...register('invoicing_postal_code', required)}
          errors={errors.invoicing_postal_code}
        />
        <Input
          label={t('general.address.city')}
          required
          {...register('invoicing_city', required)}
          errors={errors.invoicing_city}
        />
      </InputRow>
      <CountrySelect
        label={t('general.address.country')}
        required
        errors={errors.invoicing_country}
        countries={invoicingCountries}
        {...register('invoicing_country', required)}
      />
    </>
  )
}

export const FormSectionHeading = styled.h3`
  margin: 16px 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const InputRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const MissingBicHint = styled.p`
  margin: -${({ theme }) => theme.spacing.xs}rem 0 ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.warning};
`
